import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { subDays } from 'date-fns'

import { getVisionExceptionsTabUrl } from 'router/routes'
import T from 'T'

import { ChartColumnIcon, LayoutListIcon } from './icons/VisionIcons'
import { DetectionDateRange, DetectionStatus, DetectionType, LogIssueReason } from './types'

import type { SideNavMenuItem } from 'components/common/side-nav/MenuGroup'

export const EXCEPTIONS_TAB_SLUG = 'dashboard' as const
export const EXCEPTIONS_LIST_SLUG = 'exceptions' as const
export const DISCREPANCIES_LIST_SLUG = 'discrepancies' as const
export const ALL_DETECTIONS_SLUG = 'all-detections' as const
export const VERIFIED_SERVICES_SLUG = 'verified' as const
export const INSPECTIONS_SLUG = 'inspections' as const

export type ExceptionsTabSlug =
  | typeof EXCEPTIONS_TAB_SLUG
  | typeof EXCEPTIONS_LIST_SLUG
  | typeof DISCREPANCIES_LIST_SLUG
  | typeof VERIFIED_SERVICES_SLUG
  | typeof ALL_DETECTIONS_SLUG
  | typeof INSPECTIONS_SLUG

// `exact` because the tabs share a url prefix, which MenuGroup's default match would light up.
export const REVIEW_MENU: SideNavMenuItem[] = [
  { label: T.EXCEPTIONS_DASHBOARD, url: getVisionExceptionsTabUrl(EXCEPTIONS_TAB_SLUG), exact: true, Icon: ChartColumnIcon },
  { label: T.EXCEPTIONS, url: getVisionExceptionsTabUrl(EXCEPTIONS_LIST_SLUG), exact: true, Icon: WarningAmberRoundedIcon },
  { label: T.DISCREPANCIES, url: getVisionExceptionsTabUrl(DISCREPANCIES_LIST_SLUG), exact: true, Icon: SyncAltRoundedIcon },
  { label: T.VERIFIED_SERVICES, url: getVisionExceptionsTabUrl(VERIFIED_SERVICES_SLUG), exact: true, Icon: TaskAltRoundedIcon },
  { label: T.ALL_DETECTIONS, url: getVisionExceptionsTabUrl(ALL_DETECTIONS_SLUG), exact: true, Icon: LayoutListIcon },
]

export const VISION_REVIEW_SLUGS: ExceptionsTabSlug[] = [
  EXCEPTIONS_TAB_SLUG,
  EXCEPTIONS_LIST_SLUG,
  DISCREPANCIES_LIST_SLUG,
  VERIFIED_SERVICES_SLUG,
  ALL_DETECTIONS_SLUG,
]

export const INSPECTIONS_MENU_ITEM: SideNavMenuItem = {
  label: T.INSPECTIONS,
  url: getVisionExceptionsTabUrl(INSPECTIONS_SLUG),
  exact: true,
  Icon: AssignmentTurnedInOutlinedIcon,
}

export const STATUS_LABELS: Record<DetectionStatus, string> = {
  pending: T.PENDING,
  confirmed: T.CONFIRMED,
  'auto-confirmed': T.AUTO_CONFIRMED,
  rejected: T.REJECTED,
  flagged: T.FLAGGED,
  modified: T.MODIFIED,
}

// VIS-4: Log Issue reason chips. The order here drives the rendered chip
// order — kept in the AC's order ("Wrong work order · Wrong exception
// type · Wrong discrepancy · Other") so the form layout is predictable.
export const LOG_ISSUE_REASON_OPTIONS: { value: LogIssueReason; label: string }[] = [
  { value: 'wrong-work-order', label: T.WRONG_WORK_ORDER },
  { value: 'wrong-exception-type', label: T.WRONG_EXCEPTION_TYPE },
  { value: 'wrong-discrepancy', label: T.WRONG_DISCREPANCY },
  { value: 'other', label: T.OTHER },
]

export const LOG_ISSUE_REASON_LABELS: Record<LogIssueReason, string> = LOG_ISSUE_REASON_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {} as Record<LogIssueReason, string>
)

// `toISOString()` is UTC and shifts the bound by a day near midnight.
const toIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

// Calendar days, not milliseconds: a DST transition inside the window shifts it by a day.
const DEFAULT_RANGE_DAYS = 90
const today = new Date()

export const DEFAULT_DETECTION_DATE_RANGE: DetectionDateRange = {
  startDate: toIsoDate(subDays(today, DEFAULT_RANGE_DAYS)),
  endDate: toIsoDate(today),
}

// Placeholder amounts: nothing maps an exception type to a real fee yet, so the card stays display-only.
export const SURCHARGE_DEFAULTS_BY_TYPE: Partial<Record<DetectionType, number>> = {
  overload: 75,
  contamination: 100,
  'not-out': 50,
  extra: 50,
  weight: 150,
}

export const getDefaultSurchargeAmount = (type: DetectionType): number => SURCHARGE_DEFAULTS_BY_TYPE[type] ?? 0

// CRA's webpack DefinePlugin substitutes `process.env.NODE_ENV` and
// `process.env.REACT_APP_*` literals at build time. The `declare const`
// keeps TypeScript happy without pulling in `@types/node`.
declare const process: { env: Record<string, string | undefined> }

// Customer SMS/email from Vision confirm flow — separate from POST /detections/:id/review.
// Queues via POST /api/v1/messaging/broadcasts (directRecipients). Opt out with
// REACT_APP_VISION_SEND_CUSTOMER_NOTIFY=false (legacy: REACT_APP_VISION_SEND_SMS=false).
const visionSendCustomerNotifyOff =
  process.env.REACT_APP_VISION_SEND_CUSTOMER_NOTIFY === 'false' || process.env.REACT_APP_VISION_SEND_SMS === 'false'
export const VISION_SEND_CUSTOMER_NOTIFY = !visionSendCustomerNotifyOff

// Opt-in: REACT_APP_VISION_SHOW_BE_COVERAGE=true shows which denormalized fields the row carried.
export const SHOW_BACKEND_COVERAGE = process.env.REACT_APP_VISION_SHOW_BE_COVERAGE === 'true'
